"use client";

import { ReactNode, useEffect } from "react";
import { usePathname } from "next/navigation";
import { CurrentAccountIdProvider } from "@zapier/identity";
import * as Sentry from "@sentry/react";

import {
  Layout as MarketingLayout,
  MarketingSiteLayoutStylesheet,
  LayoutData,
} from "@zapier/marketing-site-layout";

import {
  TrackRouting,
  initializeDatadogRum,
  initializeSentry,
  nextJsReportWebVitals,
} from "../observability";
import { isDatadog } from "../utils/isDatadog";
import { useAmplitude } from "src/observability/Amplitude/useAmplitude";
import { useSessionContext } from "src/contexts/SessionContext";

interface Props {
  children: ReactNode;
  layoutData?: LayoutData;
}

const shouldInitSentry = !isDatadog();
if (shouldInitSentry) {
  initializeSentry({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN || "",
    commitHash: process.env.NEXT_PUBLIC_COMMIT_SHA || "",
    environment: process.env.NEXT_PUBLIC_APP_ENV || "",
  });
}

// Measure and report web vitals that Next.js tracks to Datadog.
// See: https://nextjs.org/docs/advanced-features/measuring-performance
initializeDatadogRum();
export const reportWebVitals = nextJsReportWebVitals;

function AppLayout({ children }: Props) {
  const { session } = useSessionContext();
  const pathname = usePathname();

  // Initialize Amplitude with user and account IDs
  useAmplitude({
    userId: session.id?.toString() || "",
    accountId: session.current_account_id?.toString() || "",
  });

  useEffect(() => {
    // Remove the style attribute from the button to show the manage cookies button in the UniversalFooter, copied from the Google Tag Manager approach for consistency:
    // https://gitlab.com/zapier/design-systems/design-system/-/blob/main/packages/universal-layout/src/universal-footer/components/UniversalFooter/UniversalFooter.tsx#L209
    const manageCookiesButton = document.querySelector(".ot-sdk-show-settings");
    if (manageCookiesButton) {
      manageCookiesButton.removeAttribute("style");
    }
  }, []);

  return (
    <CurrentAccountIdProvider>
      <TrackRouting>
        <MarketingSiteLayoutStylesheet />
        <MarketingLayout currentPath={pathname ?? undefined}>
          {children}
        </MarketingLayout>
      </TrackRouting>
    </CurrentAccountIdProvider>
  );
}

export default Sentry.withProfiler(AppLayout);
